import { useMemo } from 'react';
import { Offer } from '@funtech-int/yango-shared';

import { getAnnualPrice } from '../lib/offers';

import { useI18n } from './useOnboardingI18n';

const ANNUAL_PERIODS = new Set(['P1Y', 'P360D']);

function parseNumeric(value: string | undefined): number | undefined {
    if (value === undefined) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function isAnnualByMonthOffer(
    priceType: string | undefined,
    tariffPeriod: string | undefined
): boolean {
    return priceType === 'annualByMonth' && !!tariffPeriod && ANNUAL_PERIODS.has(tariffPeriod);
}

export function useOfferDisplayData(offer: Offer) {
    const i18n = useI18n();

    return useMemo(() => {
        const { offerTexts } = offer;
        const { commonPrice, commonPeriod } = offer.catalogCompositeOffer?.tariffOffer ?? {};
        const custom = offerTexts?.customTexts;

        const currencyRaw = commonPrice?.currency;
        const priceRaw = commonPrice?.amount;
        const introPriceRaw = parseNumeric(custom?.introPrice);

        const annualByMonth = isAnnualByMonthOffer(custom?.priceType, commonPeriod);

        const effectivePeriod = annualByMonth ? 'P1M' : commonPeriod;
        const effectivePrice = annualByMonth ? getAnnualPrice(introPriceRaw || priceRaw) : priceRaw;

        return {
            title: offerTexts?.subscriptionName,
            subtitle: custom?.description,
            additionalText: custom?.additionalText,
            priceText: custom?.priceText,
            badgeText: custom?.badgeText,
            innerBadgeText: custom?.innerBadgeText,
            oldPrice: custom?.oldPrice,
            price: effectivePrice,
            period: i18n('subscriptionPeriod', { period: effectivePeriod }),
            currency: i18n('currencyCode', { currency: currencyRaw }),
        };
    }, [offer, i18n]);
}
